// ./src/components/features/EventMap/MapElementSelect/MapElementSelect.tsx
//
// Picks one of an event's on-site map elements (stage, booth, washroom…). Used in
// the schedule slot drawer to link a session to where it happens (paired with the
// free-text Track). Self-contained: it loads the event's elements via the
// useEventMapElements hook (no provider required).

import React, { useMemo } from "react";
import { Loader } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { useEventMapElements } from "@/hooks/useEventMapElements";
import { AppIcon, SelectField } from "@/components/generic";
import { WHOLE_SET } from "@/utils/api";

export interface MapElementSelectProps {
  eventId: string;
  value: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  description?: string;
  placeholder?: string;
  size?: string;
  clearable?: boolean;
  /** Server refusal on the map-element field — without it a listed error is swallowed. */
  error?: string | null;
}

export const MapElementSelect: React.FC<MapElementSelectProps> = ({
  eventId,
  value,
  onChange,
  label,
  description,
  placeholder,
  size = "md",
  clearable = true,
  error,
}) => {
  const { t } = useTranslation(["events", "common"]);
  const { elements, loading, lastError: elementsError } = useEventMapElements({
    initialFilters: { eventId: [eventId] },
    // A picker shows EVERY element — read whole, never a capped page (ROADMAP #118).
    pageSize: WHOLE_SET,
  });

  const data = useMemo(
    () =>
      elements
        .filter((el) => !!el.id)
        .map((el) => ({
          value: el.id as string,
          label: el.name || t("events:map.untitled", { defaultValue: "Untitled" }),
        })),
    [elements, t]
  );

  // ⚠ A failed read is not "No map elements yet": the picker says it could not load them, on the field itself. A
  //   server refusal on the field still wins — it is about the value, this is about the list.
  const readError = elementsError ? t("events:map.elementsLoadError", { defaultValue: "Couldn’t load the map elements" }) : null;

  return (
    <SelectField
      label={label ?? t("events:map.mapElement", { defaultValue: "Map element" })}
      description={description}
      placeholder={
        loading
          ? t("common:loading", { defaultValue: "Loading…" })
          : placeholder ?? t("events:map.mapElementPh", { defaultValue: "Where on the map…" })
      }
      data={data}
      value={value}
      onChange={(_name, v) => onChange(v)}
      searchable
      clearable={clearable}
      nothingFoundMessage={readError ?? t("events:map.noElements", { defaultValue: "No map elements yet" })}
      leftSection={<AppIcon iconName="MapPin" size={16} />}
      rightSection={loading ? <Loader size="md" /> : undefined}
      size={size}
      error={error || readError || undefined}
      comboboxProps={{ withinPortal: true }}
    />
  );
};
